import {all, call, put, select, takeLatest} from 'redux-saga/effects';
import {
    toggleDefaultNotification,
    selectMultiPickerSuccess,
    getAdditionalContentsSuccess,
    getAdditionalContentsFailed,
    getOffersSuccess,
    getOffersFailed,
} from 'Container/Landingpage/action';
import {setCurrentOffersFromJSON} from 'Container/OfferDisplay/saga';
import {GET_ADDITIONAL_CONTENTS, GET_OFFERS, SELECT_MULTIPICKER} from 'Container/Landingpage/constant';
import {SET_CURRENT_OFFERS_SUCCESS} from 'Container/OfferDisplay/constant';
import {
    selectRecentlyClickedOption,
    selectUserHasInternet,
    selectUserHasMobilfunk,
    selectUserHasNone,
    selectUserHasTV,
    selectUserWantsInternet,
    selectUserWantsMobilfunk,
    selectUserWantsRedplus,
    selectUserWantsTV,
} from 'Container/Landingpage/selector';
import {selectIsDefault} from 'Container/OfferDisplay/selector';
import {updateStorageSaga} from 'Container/Storage';
import {adjustMultipickerSelections} from 'Container/Landingpage/helpers/multipickerHelper';
import {
    requestJson,
    requestHtml,
} from '@vfde-sails/utils';
import { selectPageName } from 'Container/Landingpage/selector';
import { IErrorHandling } from 'Container/OfferDisplay/types';
import { trackJsonNotValid } from 'Container/Tracking/action';
import { appendAdditionalContentToDOM, isAdditionalContentToBeMocked } from './helpers/additionalContentHelper';
import { setAdditionalContentDownloaded, setCurrentOffers } from 'Container/OfferDisplay/action';
import { generateOfferId, IOfferKeyElements, trimOfferElements } from './helpers/offerDataHelper';
import { ADDITIONAL_CMS_OPTIONS_FROM_DOM } from 'Constant';
import { jsonOfferDataValidator } from './helpers/jsonOfferDataValidatorHelper';

/**
 * Interface for getOffers action
 */
 interface IGetOffersAction {
    /**./helpers/offerDataHelper
     * action type
     */
    type: string;
    /**
     * action additional param
     */
    jsonUrl: string;
}

/**
 * Export generator function getAdditionalContentFromCms
 *
 * @export getAdditionalContentFromCms
 */
export function* getAdditionalContentFromCms () {
    try {
        const { data } = require('./__mock__/additional-content-fixture.json');
        let response: string = data;
        if (!isAdditionalContentToBeMocked()) {
            response = yield call(requestHtml, ADDITIONAL_CMS_OPTIONS_FROM_DOM.content_url, { encoder: 'utf-8' });
        }

        appendAdditionalContentToDOM(response);
        yield put(getAdditionalContentsSuccess());
        yield put(setAdditionalContentDownloaded());
    } catch (err) {
        yield put(getAdditionalContentsFailed(err.message));
    }
}

/**
 * retrieves all Offers from JSON
 */
export function* getOffers (actionObj: IGetOffersAction) {
    try {
        // core parses automatically the json
        const {jsonUrl} = actionObj;
        const response = yield call(requestJson, jsonUrl);
        const offerObj = response.body;

        // check, if JSON is valid
        const jsonCheck: IErrorHandling = jsonOfferDataValidator(offerObj);
        if (false === jsonCheck.error) {
            yield put(getOffersSuccess(trimOfferElements(offerObj)));
            yield put(setCurrentOffers());
        } else {
            yield put(getOffersFailed(jsonCheck.errorMessage));
        }

    } catch (err) {
        yield put(getOffersFailed(err.message));
        const pageName = yield (select(selectPageName()));
        const trackingData = { pageName };
        yield put(trackJsonNotValid(trackingData));
    }
}

/**
 * returns all elements presenting the multipicker state as on object
 */
export function* getOfferIdFromStore () {

    const [has_mobilfunk,
        has_internet,
        has_tv,
        has_none,
        wants_mobilfunk,
        wants_internet,
        wants_tv,
        wants_redplus] =
        yield all([
            select(selectUserHasMobilfunk()),
            select(selectUserHasInternet()),
            select(selectUserHasTV()),
            select(selectUserHasNone()),
            select(selectUserWantsMobilfunk()),
            select(selectUserWantsInternet()),
            select(selectUserWantsTV()),
            select(selectUserWantsRedplus()),
        ]);

    const offerKeys: IOfferKeyElements = {
        has_mobilfunk,
        has_internet,
        has_tv,
        has_none,
        wants_mobilfunk,
        wants_internet,
        wants_tv,
        wants_redplus,
    };

    return offerKeys;
}

/**
 * sets the current status of all multipicker options
 * and starts the offer display logic afterwards
 */
export function* selectMultiPicker () {

    const offerKeys = yield call(getOfferIdFromStore);
    const recently_clicked_option = yield select(selectRecentlyClickedOption());

    const offerKeysNew = adjustMultipickerSelections(recently_clicked_option, offerKeys);

    yield put(selectMultiPickerSuccess(offerKeysNew));
    yield call(setCurrentOffersFromJSON);

    const offerKeysString = generateOfferId(offerKeysNew);
    yield call(updateStorageSaga, offerKeysString);

}

/**
 * is called by OfferDisplay-Event after one or even more offers
 * are successfully set. if the offers do not match the selection
 * in the option tabs, defaultOffers are displayed.
 * If so, a notification-box is displayed. Bit only, when at least
 * one option in each ("user_has" and "user_wants") is selected!
 */
export function* checkDefaultNotification () {

    const offerKeys = yield call(getOfferIdFromStore);
    // is Offer a default offer?
    const isDefault = yield select(selectIsDefault());

    if (false === isDefault) {
        // if the offer is not a default offer -> nothing has to be displayed
        /**
         * @TODO: displayDefaultNotification umbenennen
         */
        yield put(toggleDefaultNotification(false));
    } else {

        const userHasSelected = offerKeys.has_mobilfunk || offerKeys.has_internet || offerKeys.has_tv || offerKeys.has_none;
        const userWantsSelected = offerKeys.wants_mobilfunk || offerKeys.wants_internet || offerKeys.wants_tv || offerKeys.wants_redplus;

        if (true === userHasSelected && true === userWantsSelected) {
            yield put(toggleDefaultNotification(true));
        } else {
            yield put(toggleDefaultNotification(false));
        }
    }
}

/**
 * Waits for GET_OFFERS action to call getAPI
 *
 * @export {function} offersSaga
 */
export default function* landingpageSaga () {
    yield takeLatest(SELECT_MULTIPICKER, selectMultiPicker);
    yield takeLatest(SET_CURRENT_OFFERS_SUCCESS, checkDefaultNotification);
    yield takeLatest(GET_OFFERS, getOffers);
    yield takeLatest(GET_ADDITIONAL_CONTENTS, getAdditionalContentFromCms);
}
